import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { Card, CardContent } from "@babackapp/ui/components/card";
import useUpdateShopCustomization from "@babackapp/private-api-client/client/use-update-shop-customization";
import useTranslation from "@babackapp/translations/use-translation";
import { cn } from "@babackapp/ui/lib/utils";

type WidgetPos = "center" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";

const GRID: WidgetPos[][] = [
  ["nw", "n", "ne"],
  ["w", "center", "e"],
  ["sw", "s", "se"],
];

const DEFAULT_POS: WidgetPos = "center";

function isWidgetPos(value: string | null | undefined): value is WidgetPos {
  return (
    value === "center" ||
    value === "n" ||
    value === "ne" ||
    value === "e" ||
    value === "se" ||
    value === "s" ||
    value === "sw" ||
    value === "w" ||
    value === "nw"
  );
}

export default function WidgetPositionCard() {
  const { data: shop } = useCurrentShopSuspense();
  const { mutate: update } = useUpdateShopCustomization(shop);

  const raw = shop.preferences.customisation.widgetPosition;
  const current: WidgetPos = isWidgetPos(raw) ? raw : DEFAULT_POS;

  const handleSelect = (pos: WidgetPos) => {
    if (pos === current) return;
    update({ widgetPosition: pos });
  };

  const t = useTranslation();

  return (
    <Card className="w-full">
      <CardContent className="flex flex-col gap-4 mt-6">
        <div>
          <p className="text-sm font-medium leading-none">
            {t(
              "settings.customerPortal.customization.fields.widgetPosition.title",
            )}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              "settings.customerPortal.customization.fields.widgetPosition.description",
            )}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-1.5 w-fit">
          {GRID.flat().map((pos) => (
            <button
              key={pos}
              type="button"
              onClick={() => handleSelect(pos)}
              title={pos}
              className={cn(
                "size-12 rounded-md flex items-center justify-center transition-colors border",
                pos === current
                  ? "bg-primary/15 border-primary"
                  : "bg-muted hover:bg-primary/10 border-transparent",
              )}
            >
              <div
                className={cn(
                  "size-3 rounded-sm transition-colors",
                  pos === current ? "bg-primary" : "bg-foreground opacity-30",
                )}
              />
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
